import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Button, Chip, CircularProgress, Container, Dialog, DialogContent, DialogTitle, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { ChevronLeft, ChevronRight, Close, Edit, Folder, Today } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import AppointmentEditorDialog from "../components/AppointmentEditorDialog";
import { useNavigation } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, pageHeader, typography } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { roomMapService, RoomMap as RoomMapData, RoomMapItem } from "../services/roomMapService";

/**
 * Mapa de salas (RN-549): matriz salas × horários de 30 min de um dia. Branco = livre, azul = ocupada,
 * vermelho = superalocada (duas sessões na mesma sala e horário). Clique na célula abre o detalhe da
 * alocação com atalho para editar o agendamento (mesmo modal da agenda) — ao salvar, o mapa recarrega.
 */
const localIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const fmtDate = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
const fmtTime = (iso: string) => iso.slice(11, 16);
const minuteOf = (iso: string) => parseInt(iso.slice(11, 13), 10) * 60 + parseInt(iso.slice(14, 16), 10);
const shiftDay = (iso: string, days: number) => { const [y, m, d] = iso.split("-").map(Number); return localIso(new Date(y, m - 1, d + days)); };
const NO_ROOM = "__sem_sala__";

const CELL = { free: "#ffffff", busy: colors.primary, busySoft: "#d6f4f7", over: "#e53935", overSoft: "#fde2e1", rented: "#f1f3f5" };

type CellState = "free" | "busy" | "over" | "rented";

const RoomMap: React.FC = () => {
  const { user } = useAuth();
  const { goToDashboard, goToSchedule } = useNavigation();
  const today = localIso(new Date());
  const [date, setDate] = useState(today);
  const [teamId, setTeamId] = useState("");
  const [data, setData] = useState<RoomMapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cell, setCell] = useState<{ roomId: string; roomName: string; slot: number; items: RoomMapItem[] } | null>(null);
  const [editing, setEditing] = useState<RoomMapItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await roomMapService.get(date, teamId || undefined));
    } catch {
      setError("Não foi possível carregar o mapa de salas.");
    } finally {
      setLoading(false);
    }
  }, [date, teamId]);

  useEffect(() => { document.title = "Clinic4Us - Mapa de salas"; }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = data.rooms.map((r) => ({ id: r.id, name: r.name, rented: r.rentedToday, items: data.items.filter((i) => i.roomId === r.id) }));
    const orphans = data.items.filter((i) => !i.roomId);
    if (orphans.length > 0) list.push({ id: NO_ROOM, name: "Sem sala", rented: false, items: orphans });
    return list;
  }, [data]);

  const overbooked = useMemo(() => new Set(data?.overbooked ?? []), [data]);
  const slotMinutes = data?.slotMinutes ?? 30;

  const itemsInSlot = (items: RoomMapItem[], start: number) => items.filter((i) => minuteOf(i.start) < start + slotMinutes && minuteOf(i.end) > start);
  const stateOf = (roomId: string, rented: boolean, items: RoomMapItem[], start: number): CellState => {
    if (items.length === 0) return rented ? "rented" : "free";
    if (roomId !== NO_ROOM && overbooked.has(`${roomId}|${start}`)) return "over";
    if (items.length > 1) return "over";
    return "busy";
  };

  const totals = useMemo(() => {
    if (!data) return { sessions: 0, over: 0, noRoom: 0 };
    return { sessions: data.items.length, over: overbooked.size, noRoom: data.items.filter((i) => !i.roomId).length };
  }, [data, overbooked]);

  const initials = (name: string | null) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  const handleRevalidateLogin = () => {
    localStorage.removeItem("clinic4us-user-session");
    localStorage.removeItem("clinic4us-remember-me");
    window.location.href = "/?page=login";
  };

  const legend = (color: string, border: string, label: string) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: "0.8rem", color: colors.textSecondary }}>
      <Box sx={{ width: 16, height: 16, borderRadius: "4px", background: color, border: `1px solid ${border}` }} />{label}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal showCTAButton={false} className="login-header" isLoggedIn={true} notificationCount={0} onRevalidateLogin={handleRevalidateLogin} onLogoClick={goToDashboard} />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters sx={pageHeader.container}>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">Mapa de salas</Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Ocupação das salas de meia em meia hora. Clique numa célula para ver quem está alocado e editar o agendamento.
              </Typography>
            </Box>
            <Box className="c4u-page-header__actions"><FaqButton /></Box>
          </Box>

          <div className="c4u-panel">
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Tooltip title="Dia anterior"><IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => setDate(shiftDay(date, -1))} aria-label="Dia anterior"><ChevronLeft fontSize="small" /></IconButton></Tooltip>
                <TextField size="small" type="date" label="Dia" value={date} onChange={(e) => e.target.value && setDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, flex: "0 0 170px", minWidth: 170 }} />
                <Tooltip title="Próximo dia"><IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => setDate(shiftDay(date, 1))} aria-label="Próximo dia"><ChevronRight fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Hoje"><IconButton className="c4u-btn-icon c4u-btn-icon--outline" onClick={() => setDate(today)} disabled={date === today} aria-label="Hoje"><Today fontSize="small" /></IconButton></Tooltip>
              </Box>
              <TextField select size="small" label="Equipe" value={teamId} onChange={(e) => setTeamId(e.target.value)} InputLabelProps={{ shrink: true }} SelectProps={{ displayEmpty: true }} sx={{ ...filterFieldSx, flex: "0 0 220px", minWidth: 180 }}>
                <MenuItem value="">Todas</MenuItem>
                {(data?.teams ?? []).map((t) => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
              </TextField>
              <Box sx={{ flex: 1 }} />
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {legend(CELL.free, colors.border, "Livre")}
                {legend(CELL.busy, CELL.busy, "Ocupada")}
                {legend(CELL.over, CELL.over, "Superalocada")}
                {legend(CELL.rented, colors.border, "Alugada")}
              </Box>
            </Box>

            {data && !loading && (
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                <Chip size="small" label={`${data.weekday}, ${fmtDate(data.date)}`} sx={{ fontWeight: 600 }} />
                <Chip size="small" label={`${totals.sessions} sessão(ões)`} sx={{ bgcolor: CELL.busySoft, color: colors.primary, fontWeight: 600 }} />
                <Chip size="small" label={totals.over > 0 ? `${totals.over} horário(s) superalocado(s)` : "Sem superalocação"} sx={{ bgcolor: totals.over > 0 ? CELL.overSoft : colors.successSoft, color: totals.over > 0 ? CELL.over : "#155724", fontWeight: 600 }} />
                {totals.noRoom > 0 && <Chip size="small" label={`${totals.noRoom} sem sala`} sx={{ bgcolor: colors.warningSoft, color: colors.warningSoftText, fontWeight: 600 }} />}
              </Box>
            )}

            {error && <Box className="c4u-info-box c4u-info-box--warning" sx={{ mb: 2 }}><Typography>{error}</Typography></Box>}

            {loading || !data ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress size={28} sx={{ color: colors.primary }} /></Box>
            ) : rows.length === 0 ? (
              <Typography sx={{ color: colors.textSecondary, py: 4, textAlign: "center" }}>Nenhuma sala ativa cadastrada. Cadastre as salas em Administrativo &gt; Salas.</Typography>
            ) : (
              <Box sx={{ overflowX: "auto", border: `1px solid ${colors.borderLight}`, borderRadius: "10px" }}>
                <Box component="table" sx={{ borderCollapse: "separate", borderSpacing: 0, minWidth: 160 + data.slots.length * 44, width: "100%", tableLayout: "fixed" }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" sx={{ position: "sticky", left: 0, zIndex: 2, background: colors.backgroundSubtle, textAlign: "left", p: 1, width: 160, minWidth: 160, fontSize: "0.8rem", color: colors.textSecondary, borderBottom: `1px solid ${colors.borderLight}`, borderRight: `1px solid ${colors.borderLight}` }}>Sala</Box>
                      {data.slots.map((s) => (
                        <Box component="th" key={s.startMinute} sx={{ p: 0.5, fontSize: "0.7rem", fontWeight: s.startMinute % 60 === 0 ? 700 : 400, color: s.startMinute % 60 === 0 ? colors.textStrong : colors.textMuted, background: colors.backgroundSubtle, borderBottom: `1px solid ${colors.borderLight}`, borderLeft: s.startMinute % 60 === 0 ? `1px solid ${colors.border}` : `1px dashed ${colors.borderLight}`, whiteSpace: "nowrap" }}>
                          {s.startMinute % 60 === 0 ? s.time : ""}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {rows.map((room) => (
                      <Box component="tr" key={room.id}>
                        <Box component="th" sx={{ position: "sticky", left: 0, zIndex: 1, background: "#fff", textAlign: "left", p: 1, fontSize: "0.85rem", fontWeight: 600, color: room.id === NO_ROOM ? colors.warningSoftText : colors.textStrong, borderBottom: `1px solid ${colors.borderLight}`, borderRight: `1px solid ${colors.borderLight}`, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {room.name}{room.rented ? <Typography component="span" sx={{ display: "block", fontSize: "0.7rem", color: colors.textMuted, fontWeight: 400 }}>alugada hoje</Typography> : null}
                        </Box>
                        {data.slots.map((s) => {
                          const items = itemsInSlot(room.items, s.startMinute);
                          const state = stateOf(room.id, room.rented, items, s.startMinute);
                          const first = items[0];
                          const starts = first ? minuteOf(first.start) === s.startMinute : false;
                          const bg = state === "busy" ? CELL.busy : state === "over" ? CELL.over : state === "rented" ? CELL.rented : CELL.free;
                          const title = items.length === 0 ? `${s.time} · livre` : items.map((i) => `${fmtTime(i.start)}–${fmtTime(i.end)} ${i.patientName || "—"} · ${i.professionalName || "—"}`).join("\n");
                          return (
                            <Tooltip key={s.startMinute} title={<span style={{ whiteSpace: "pre-line" }}>{title}</span>} arrow disableInteractive>
                              <Box component="td" onClick={() => items.length > 0 && setCell({ roomId: room.id, roomName: room.name, slot: s.startMinute, items })}
                                sx={{
                                  height: 44, p: 0, cursor: items.length > 0 ? "pointer" : "default", background: bg,
                                  borderBottom: `1px solid ${colors.borderLight}`, borderLeft: s.startMinute % 60 === 0 ? `1px solid ${colors.border}` : `1px dashed ${colors.borderLight}`,
                                  color: "#fff", fontSize: "0.7rem", fontWeight: 600, textAlign: "center", verticalAlign: "middle", overflow: "hidden",
                                  "&:hover": items.length > 0 ? { filter: "brightness(0.92)" } : undefined,
                                }}>
                                {items.length > 0 && (starts || s.startMinute === data.openStartMinute) ? (
                                  <Box sx={{ px: 0.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {items.length > 1 ? `${items.length}×` : initials(first.patientName)}
                                  </Box>
                                ) : null}
                              </Box>
                            </Tooltip>
                          );
                        })}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            )}
          </div>
        </Container>
      </Box>

      <Dialog open={cell !== null} onClose={() => setCell(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "12px", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" } }}>
        <DialogTitle sx={{ background: cell && cell.items.length > 1 && cell.roomId !== NO_ROOM ? CELL.over : colors.primary, color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "1.25rem 1.5rem", fontSize: "1.1rem", fontWeight: typography.fontWeight.semibold }}>
          <Box>
            {cell?.roomName} · {cell ? `${String(Math.floor(cell.slot / 60)).padStart(2, "0")}:${String(cell.slot % 60).padStart(2, "0")}` : ""}
            {cell && cell.items.length > 1 && cell.roomId !== NO_ROOM && <Typography component="span" sx={{ display: "block", fontSize: "0.8rem", fontWeight: 400 }}>Superalocada: {cell.items.length} sessões no mesmo horário</Typography>}
          </Box>
          <IconButton onClick={() => setCell(null)} sx={{ color: "#fff", padding: "0.25rem" }} aria-label="Fechar"><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: "1.25rem !important", display: "flex", flexDirection: "column", gap: 1.25 }}>
          {cell?.items.map((i) => (
            <Box key={i.scheduleId} sx={{ border: `1px solid ${colors.borderLight}`, borderRadius: "10px", p: 1.5, display: "flex", gap: 1.5, alignItems: "center" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ fontWeight: 700 }}>{fmtTime(i.start)} – {fmtTime(i.end)} · {i.patientName || "—"}</Box>
                <Box sx={{ fontSize: "0.85rem", color: colors.textSecondary }}>{[i.professionalName, i.serviceName].filter(Boolean).join(" · ") || "—"}</Box>
                <Box sx={{ display: "flex", gap: 0.5, mt: 0.75, flexWrap: "wrap" }}>
                  {i.confirmation && <Chip size="small" label={i.confirmation} sx={{ height: 22, fontSize: "0.7rem" }} />}
                  {i.presence && i.presence !== "Não informado" && <Chip size="small" label={i.presence} sx={{ height: 22, fontSize: "0.7rem", bgcolor: colors.successSoft, color: "#155724" }} />}
                  {i.roomFromAssignment && <Chip size="small" label="sala padrão do profissional" sx={{ height: 22, fontSize: "0.7rem", bgcolor: colors.backgroundAlt, color: colors.textSecondary }} />}
                  {!i.roomId && <Chip size="small" label="sem sala" sx={{ height: 22, fontSize: "0.7rem", bgcolor: colors.warningSoft, color: colors.warningSoftText }} />}
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 0.75 }}>
                <Tooltip title="Editar agendamento"><IconButton size="small" onClick={() => setEditing(i)} sx={{ bgcolor: colors.primary, color: "#fff", width: 36, height: 36, "&:hover": { bgcolor: colors.primaryHover } }}><Edit fontSize="small" /></IconButton></Tooltip>
                <Tooltip title="Abrir a agenda"><IconButton size="small" onClick={() => goToSchedule(i.patientName || undefined)} sx={{ bgcolor: "#03a9f4", color: "#fff", width: 36, height: 36 }}><Folder fontSize="small" /></IconButton></Tooltip>
              </Box>
            </Box>
          ))}
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button onClick={() => setCell(null)} variant="outlined" sx={{ textTransform: "none", borderColor: colors.border, color: colors.textSecondary }}>Fechar</Button>
          </Box>
        </DialogContent>
      </Dialog>

      {editing && (
        <AppointmentEditorDialog
          scheduleId={editing.scheduleId}
          patientId={editing.patientId || ""}
          patientName={editing.patientName || ""}
          entityId={user?.entityId || ""}
          onClose={() => setEditing(null)}
          onChanged={() => { setCell(null); load(); }}
        />
      )}

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default RoomMap;
